import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AppRouter from './components/AppRouter';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import AppointmentPage from './pages/AppointmentPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetail from './components/ServiceDetail';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import TestimonialsPage from './pages/TestimonialsPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLogin from './pages/AdminLogin';
import { SkeletonText } from './components/SkeletonLoader';
import './index.css';

// Lazy load heavy components
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const BlogPostEditor = lazy(() => import('./pages/BlogPostEditor'));
const BloggerImport = lazy(() => import('./pages/BloggerImport'));
const ShopItemEditor = lazy(() => import('./pages/ShopItemEditor'));
const PageContentEditor = lazy(() => import('./pages/PageContentEditor'));
const CommentModeration = lazy(() => import('./pages/CommentModeration'));

const LoadingFallback = () => (
  <div style={{ padding: '40px', textAlign: 'center' }}>
    <SkeletonText lines={5} />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <AppRouter>
      <Routes>
      {/* Admin routes without Layout */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/new" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <BlogPostEditor />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/edit/:slug" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <BlogPostEditor />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/import" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <BloggerImport />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/shop/new" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <ShopItemEditor />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/shop/edit/:id" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <ShopItemEditor />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/content" 
        element={<Navigate to="/admin?tab=content" replace />} 
      />
      <Route 
        path="/admin/content/:pageId/:sectionId" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <PageContentEditor />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/comments" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <CommentModeration />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Public routes with Layout */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route 
        path="/blog/:slug" 
        element={
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <BlogPostPage />
            </Suspense>
          </Layout>
        } 
      />
      <Route path="/appointment" element={<Layout><AppointmentPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
      <Route path="/services/:serviceId" element={<Layout><ServiceDetail /></Layout>} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
      <Route path="/testimonials" element={<Layout><TestimonialsPage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route 
        path="/shop" 
        element={
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <ShopPage />
            </Suspense>
          </Layout>
        } 
      />
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
      </Routes>
    </AppRouter>
  </ErrorBoundary>
);

export default App;

